import React, {useEffect, useState} from 'react';
import { Grid } from '../../../../../kit/Grid';
import {
    HSAdministeredDrug,
    HSDoseRound,
    HSPackedMedication,
    ReasonCode,
    SyringeDriverRunDto,
} from 'server-openapi';
import { MedicationInformationBox } from '../../medicationInformation/MedicationInformationBox';
import { DateUtils } from '../../../../../core/utils/dateUtils';
import { CategoryHeading } from './ContinuousMedicationList';
import { useSyncCenter } from '../../../../../syncstream/SyncCenterProvider';
import { useStore } from '../../../../../core/storage/hooks/UseStore';
import { MedicationListProps } from '../MedicationListsTabbedRouter';
import { useApiUtils } from '../../../../../syncstream/utils/hooks/useApiUtils';
import itiriri from 'itiriri';
import { MedicationGroup } from '../../../../../syncstream/utils/ResidentDetailsUtils';
import { colors } from '../../../../../kit/Theme/Theme';
import { EnqueuedDrugCreateData } from '../../../../../syncstream/SyncRounds';
import {format, isSameDay} from "date-fns";
import {getRoundHierarchiesForPackedMedication} from "../../../../../view_model/RoundHierarchy";
import { SyringeSelectionProvider } from "../../medicationInformation/SyringeDriverSelectionScope";

interface SyringeDriversMedicationListProps extends MedicationListProps {
  onAdministerDrugs: (drugs: HSAdministeredDrug[], doseTimestamp: string) => Promise<EnqueuedDrugCreateData | undefined>;
  groupedPackedMedicationList: MedicationGroup[];
  lastTwoPackedPatientDaysMedication: HSPackedMedication[];
  displayAllActions?: boolean;
  displayAllActivity: boolean;
}

export function SyringeDriversMedicationList(props: SyringeDriversMedicationListProps) {
  const services = useSyncCenter();
  const apiUtils = useApiUtils();

  const roundsStore = useStore(services.rounds.store).store;
  const drugStore = useStore(services.drugs.store).store;
  const syringeDriverRunStore = useStore(services.syringeDriverRun.store).store;
  
  //  hash of packed medication id to syringe driver run id.
  const [pmDriverHash, setPmDriverHash] = useState<Record<string, SyringeDriverRunDto>>({});
  // hash of syringe driver run to lists of pm.
  const [driverPmHash, setDriverPmHash] = useState<Record<string, HSPackedMedication[]>>({});
    
    useEffect(() => {
        var matched = false;
        var newPmDriverHash:Record<string, SyringeDriverRunDto> = {};
        var newDriverPmHash:Record<string, HSPackedMedication[]> = {};
        
        for(let grp of props.groupedPackedMedicationList) {
            if (grp.categories.isSyringeDriver) {
                for(let pm of grp.medications) {
                    // Try and find a syringe driver run.
                    var roundHierarchies = getRoundHierarchiesForPackedMedication(roundsStore, pm);
                    var syringeDriverRun = itiriri(syringeDriverRunStore.values())
                        .filter(x => isSameDay(props.selectedDate, DateUtils.toDate(x.startedAt!)) ||
                            (!!x.scheduledEnd && isSameDay(props.selectedDate, DateUtils.toDate(x.scheduledEnd!))))
                        .find(
                            (run) => !!run.administeredDrugs?.find(x => (roundHierarchies.some(roundHierarchy => x.administeredDrugClinicalSystemId === roundHierarchy?.administeredDrug.clinicalSystemId || x.administeredDrugId === roundHierarchy?.administeredDrug.hsId)))
                        );
                    if (syringeDriverRun) {
                        matched = true;
                        newPmDriverHash[pm.hsId!] = syringeDriverRun;
                        newDriverPmHash[syringeDriverRun.clinicalSystemId!] ??= [];
                        newDriverPmHash[syringeDriverRun.clinicalSystemId!].push(pm);
                        newDriverPmHash[syringeDriverRun.clinicalSystemId!] = newDriverPmHash[syringeDriverRun.clinicalSystemId!].sort((a, b) => {
                            var aNm = drugStore.get(a.drugHsId!.toString())?.name ?? "";
                            var bNm = drugStore.get(b.drugHsId!.toString())?.name ?? "";
                            return aNm.localeCompare(bNm);
                        }) 
                    }
                }
            }
        }
        setDriverPmHash(newDriverPmHash)
        setPmDriverHash(newPmDriverHash);
    }, [props.groupedPackedMedicationList, props.selectedDate]);

  const showSyringeDriver = (pm: HSPackedMedication): boolean => {
      const sdr = pmDriverHash[pm.hsId!];
      if (!sdr) {
          return true;
      }
      if (!driverPmHash[sdr.clinicalSystemId!]) {
          return true;
      }
      return driverPmHash[sdr.clinicalSystemId!]!.findIndex(x => x.hsId! == pm.hsId!) == 0;
  }
  const packedMedicationsForSyringeDriverRun = (pm: HSPackedMedication) => {
        const sdr = pmDriverHash[pm.hsId!];
        if (!sdr) {
            return undefined;
        }
        return driverPmHash[sdr.clinicalSystemId!];
  }
  
  const roundWindow = props.currentRound?.createdAt
    ? apiUtils.rounds.getRoundWindow(DateUtils.toDate(props.currentRound.createdAt), props.facilityGroupId)
    : undefined;
  const syringeDrivers = props.groupedPackedMedicationList.filter(
    (group, index) =>
      group.categories.isSyringeDriver && 
      (props.displayAllActions ||
        !group.syringeDriverRunScheduledActivity ||
        !roundWindow ||
        (!!group.syringeDriverRunScheduledActivity && 
         (DateUtils.toDate(roundWindow.start) <= DateUtils.toDate(group.syringeDriverRunScheduledActivity.scheduledAt!) &&
         (DateUtils.toDate(roundWindow.end) >= DateUtils.toDate(group.syringeDriverRunScheduledActivity.scheduledAt!)))
        )
      )
  );
  const getSyringeDriverName = (medicationGroup: MedicationGroup): string => {

      if (medicationGroup.medications && medicationGroup.medications.length > 0) {
          var syringeDriverRun = pmDriverHash[medicationGroup.medications[0].hsId!];
          if (!!syringeDriverRun) {
              return `Syringe Driver`;
          }
      }
      
      return `Syringe Drivers ${medicationGroup.categories.profileNumber ? `${medicationGroup.categories.profileNumber} ` : ''}(${
          apiUtils.residentDetails.getTotalDoseAmounts(medicationGroup.medications)
           ?? 0})`;
  }

    return (
    <Grid cols={1} gap={1}>
      {syringeDrivers.map((medicationGroup, index) => {

          // Create a stable, unique scopeId per group/activity
          const scopeId = `syringe-${props.patient.hsId}-${props.selectedDate.toISOString()}-${medicationGroup.categories.profileNumber ?? 'grp'}-${index}`;

          return (
              <Grid cols={1} gap={0.5} key={index}>
                  <CategoryHeading weight={'bold'}>
                      {`${
                          medicationGroup.categories.doseDate && DateUtils.dateTo24HourTimeString(medicationGroup.categories.doseDate)
                      } ${getSyringeDriverName(medicationGroup)}`}
                  </CategoryHeading>
                  <SyringeSelectionProvider>
                      {medicationGroup.medications.filter(x => showSyringeDriver(x)).map((packedMedication, idx) => {
                              return (
                                  <SyringeDriverMedicationInformation
                                      key={idx}
                                      packedMedication={packedMedication}
                                      packedMedications={medicationGroup.medications}
                                      syringeDriverRun={pmDriverHash[packedMedication.hsId!]}
                                      packedMedicationsToShow={packedMedicationsForSyringeDriverRun(packedMedication)}
                                      currentDosedDrug={apiUtils.rounds.getMostRecentDrugInRoundByPackedMedication(
                                          packedMedication,
                                          props.patient.hsId!,
                                          props.currentRound,
                                      )}
                                      onAdminister={props.onAdministerDrugs}
                                      roundsStore={roundsStore}
                                      status={apiUtils.rounds.getPackedMedicationStatus(packedMedication, props.patient.hsId!)}
                                      {...(props as MedicationListProps)}
                                      doseTime={medicationGroup.categories.doseDate ? DateUtils.dateTo24HourTimeString(medicationGroup.categories.doseDate) : undefined}
                                      displayAllActivity={props.displayAllActivity}
                                      // Pass scopeId so selection of drugs in the syringe driver for this dialog/group is isolated
                                      selectionSyringeDriverDrugsScopeId={scopeId}
                                  />
                              )
                          }
                      )}
                  </SyringeSelectionProvider>
              </Grid>
          );
      })}
    </Grid>
  );
}

interface SyringeDriverMedicationInformationProps extends MedicationListProps {
  packedMedication: HSPackedMedication;
  syringeDriverRun?: SyringeDriverRunDto;
  packedMedicationsToShow?: HSPackedMedication[]; 
  packedMedications?: HSPackedMedication[];
  currentDosedDrug?: HSAdministeredDrug;
  onAdminister?: (drugs: HSAdministeredDrug[], doseTimestamp: string) => Promise<EnqueuedDrugCreateData | undefined>;
  roundsStore: ReadonlyMap<string, HSDoseRound>;
  status?: ReasonCode;
  displayAllActivity?: boolean;
  doseTime?: string;
}

export function SyringeDriverMedicationInformation(props: SyringeDriverMedicationInformationProps & { selectionSyringeDriverDrugsScopeId?: string }){

function getStatusLabel() {
    if (props.syringeDriverRun) {
        switch(props.syringeDriverRun.status) {
        case "Paused":
            return { label: 'PAUSED', colour: colors.text_white, backgroundColour: colors.bg_purple_700 };
        case "Stopped":
            return { label: 'STOPPED', colour: colors.text_white, backgroundColour: colors.bg_purple_700 };
        case "Running":
            return { label: 'RUNNING', colour: colors.text_black, backgroundColour: colors.bg_green_700 };
        default:
            return  undefined;
        }
    }
    return undefined;
  }
  const getInfoLabel= () => {
    if (props.syringeDriverRun) {
        const userUtils = useApiUtils().users;
        const startedAt = DateUtils.toDate(props.syringeDriverRun.clientCreatedAt!);
        let timeDescription = DateUtils.dateStringTo24HourTimeString(props.syringeDriverRun.clientCreatedAt!);
        if (!isSameDay(new Date(), startedAt)) {
            timeDescription += " on " + format(startedAt, 'EEEE')
        }
        return `Applied to ${props.syringeDriverRun.site?.position} at ${timeDescription} by ${userUtils.getUserName(props.syringeDriverRun.clientUser)}`;
    }
    return undefined;
  }

  return (
    <MedicationInformationBox
      // Provide group-scoped syringe driver drug selection id to the box
      selectionSyringeDriverDrugsScopeId={props.selectionSyringeDriverDrugsScopeId}
      infoLabel={getInfoLabel()}
      packedMedication={props.packedMedication}
      packedMedicationsToShow={props.packedMedicationsToShow}
      packedMedications={props.packedMedications}
      currentDosedDrug={props.currentDosedDrug}
      onAdminister={props.onAdminister}
      scheduledTime={props.packedMedication.doseTimestamp}
      syringeDriverRun={props.syringeDriverRun}
      status={props.status}
      statusLabel={getStatusLabel()}
      {...(props as MedicationListProps)}
      doseTime={props.doseTime as string}
      displayAllActivity={props.displayAllActivity ?? false}
    />
  );
}
